'use client';

// Porous-absorber flow resistivity (σ) control — used by the room editor's
// CustomPanelDesigner and the public porous-absorber-calculator tool. Both
// used to hand-roll their own numeric <input> (clamping on every keystroke,
// which made it impossible to type a number below the minimum — e.g.
// deleting "17000" down to "1" snapped straight back to 1000 mid-edit) plus
// a separately hand-rolled `<input type="range">`. This is that logic pulled
// into one place, built entirely on `Slider`/`SliderValueField`
// (AGENTS.md's "sliders must always use ui/Slider.tsx" rule) — the readout
// is click-to-edit (type anything, including something transiently below
// min, and it only clamps on commit — Enter/blur), same as every other
// numeric slider readout in the app.
//
// Presentation (label markup, unit label text, colour, testid prefix, preset
// chip styling) is intentionally still per-caller — the room editor's dark
// chrome (`ed-*` tokens) and the public tool's light marketing page
// (`rt-*` tokens) are different design systems, so this isn't trying to force
// one visual onto both. Only the clamp/snap/parse math and the click-to-edit
// mechanics are shared, which is where the actual bug lived.

import Slider, { SliderValueField } from './Slider';
import { MATERIAL_PRESETS } from '@roomtreatment/floorplan-core';
import type { ReactNode } from 'react';

export const SIGMA_MIN = 1000;
export const SIGMA_MAX = 60000;
const SIGMA_STEP = 500;

export function clampSigma(v: number): number {
  return Math.max(SIGMA_MIN, Math.min(SIGMA_MAX, Math.round(v)));
}

function snapSigma(v: number): number {
  return clampSigma(Math.round(v / SIGMA_STEP) * SIGMA_STEP);
}

function parseSigma(raw: string): number | null {
  const n = parseFloat(raw.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export interface FlowResistivityFieldProps {
  sigma: number;
  onChange: (sigma: number) => void;
  /** Full styled label element — kept caller-owned since the two call sites
   *  use different design-token families. */
  label: ReactNode;
  /** e.g. "rayl/m" or "Pa·s/m²" — same physical unit, different label
   *  convention per caller. */
  unitLabel: string;
  color?: string;
  testIdPrefix: string;
  captionClassName?: string;
  inputClassName?: string;
  presetClassName: (active: boolean) => string;
}

export default function FlowResistivityField({
  sigma,
  onChange,
  label,
  unitLabel,
  color = 'var(--ed-accent)',
  testIdPrefix,
  captionClassName = 'ed-caption ed-mono',
  inputClassName,
  presetClassName,
}: FlowResistivityFieldProps) {
  const commit = (v: number) => onChange(clampSigma(v));

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        {label}
        <div className="flex items-center gap-1">
          <SliderValueField
            value={sigma}
            onCommit={commit}
            format={(v) => String(Math.round(v))}
            parse={parseSigma}
            min={SIGMA_MIN}
            max={SIGMA_MAX}
            ariaLabel="Flow resistivity"
            testId={`${testIdPrefix}-sigma-input`}
            className={inputClassName}
          />
          <span className={captionClassName}>{unitLabel}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {MATERIAL_PRESETS.map((m) => {
          const active = m.sigma === sigma;
          return (
            <button
              key={m.key}
              type="button"
              data-testid={`${testIdPrefix}-preset-${m.key}`}
              onClick={() => onChange(m.sigma)}
              title={`${m.sigma.toLocaleString()} rayl/m`}
              className={presetClassName(active)}
            >
              {m.label}
            </button>
          );
        })}
      </div>
      <Slider
        className="mt-0.5"
        min={SIGMA_MIN}
        max={SIGMA_MAX}
        step={SIGMA_STEP}
        snap={snapSigma}
        color={color}
        editable={false}
        testId={`${testIdPrefix}-sigma-range`}
        thumbs={[
          {
            value: sigma,
            onChange: commit,
            ariaLabel: 'Flow resistivity slider',
            testId: `${testIdPrefix}-sigma-thumb`,
          },
        ]}
      />
    </div>
  );
}
